function normalizeParsedWordCount(value: number): number | undefined {
  const n = Math.floor(value);
  if (!Number.isSafeInteger(n) || n < 50) return undefined;
  return n;
}

export type ParsedExplicitWordCount = {
  targetWords?: number;
  minimumWords?: number;
};

const DESCRIPTIVE_DETERMINERS = new Set(["this", "that", "these", "those", "a", "an", "the", "my", "your", "its", "his", "her", "their"]);
const LENGTH_PREPOSITIONS = new Set([
  "into",
  "to",
  "for",
  "at",
  "about",
  "around",
  "roughly",
  "approximately",
  "under",
  "over",
  "below",
  "above",
]);
const LENGTH_TARGET_TRAILERS = new Set(["long", "total", "minimum", "max", "maximum", "target"]);
const DESCRIPTIVE_WORD_FOLLOWERS = new Set(["line", "sentence", "paragraph", "passage", "excerpt", "snippet"]);

function isWordChar(ch: string): boolean {
  const c = ch.charCodeAt(0);
  return (c >= 48 && c <= 57) || (c >= 97 && c <= 122) || c === 95 || c === 45;
}

function stripTokenPunctuation(token: string): string {
  let start = 0;
  let end = token.length;
  while (start < end && !isWordChar(token[start]!)) start += 1;
  while (end > start && !isWordChar(token[end - 1]!)) end -= 1;
  return token.slice(start, end);
}

function parseWordCountNumber(raw: string): number | undefined {
  const token = raw.trim().toLowerCase().replace(/,/g, "");
  if (!token) return undefined;
  if (token.endsWith("k") && token.length > 1) {
    const base = Number(token.slice(0, -1));
    if (Number.isFinite(base) && base > 0) {
      return normalizeParsedWordCount(Math.floor(base * 1000));
    }
    return undefined;
  }
  const n = Number(token);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return normalizeParsedWordCount(Math.floor(n));
}

function isHyphenatedDescriptiveWordCount(token: string): boolean {
  const dash = token.indexOf("-");
  if (dash <= 0) return false;
  const suffix = token.slice(dash + 1).toLowerCase();
  if (suffix !== "word" && suffix !== "words") return false;
  return parseWordCountNumber(token.slice(0, dash)) !== undefined;
}

function tokenizeForWordCountScan(text: string): string[] {
  return text
    .toLowerCase()
    .split(/\s+/)
    .map(stripTokenPunctuation)
    .filter((token) => token.length > 0 && !isHyphenatedDescriptiveWordCount(token));
}

/**
 * Deterministic scan for explicit user word-count requirements (minimum / target).
 * Returns null for descriptive mentions such as "this 16-word line".
 */
export function parseExplicitWordCountFromText(text: string): ParsedExplicitWordCount | null {
  const raw = String(text ?? "").trim();
  if (!raw) return null;

  const tokens = tokenizeForWordCountScan(raw);
  if (tokens.length === 0) return null;

  let minimumWords: number | undefined;
  let targetWords: number | undefined;

  for (let i = 0; i < tokens.length; i += 1) {
    const tok = tokens[i]!;
    if (tok !== "word" && tok !== "words") continue;

    if (tok === "word") {
      const follower = tokens[i + 1];
      if (follower && DESCRIPTIVE_WORD_FOLLOWERS.has(follower)) continue;
    }

    if (i === 0) continue;
    const numToken = tokens[i - 1]!;
    const n = parseWordCountNumber(numToken);
    if (n === undefined) continue;

    if (i >= 3 && tokens[i - 3] === "at" && tokens[i - 2] === "least") {
      minimumWords = n;
      continue;
    }
    if (i >= 3 && tokens[i - 3] === "minimum" && tokens[i - 2] === "of") {
      minimumWords = n;
      continue;
    }

    const prep = i >= 2 ? tokens[i - 2]! : "";
    const determinerBeforeNumber = i >= 2 ? tokens[i - 2]! : "";
    const trailer = tokens[i + 1] ?? "";

    if (DESCRIPTIVE_DETERMINERS.has(determinerBeforeNumber) && !LENGTH_TARGET_TRAILERS.has(trailer)) {
      continue;
    }

    const hasLengthPreposition = i >= 2 && LENGTH_PREPOSITIONS.has(prep);
    const hasTargetTrailer = LENGTH_TARGET_TRAILERS.has(trailer);
    const bareNumericTarget =
      !DESCRIPTIVE_DETERMINERS.has(determinerBeforeNumber) || hasTargetTrailer || hasLengthPreposition;

    if (hasLengthPreposition || hasTargetTrailer || bareNumericTarget) {
      targetWords = n;
    }
  }

  if (minimumWords === undefined && targetWords === undefined) return null;
  return { minimumWords, targetWords };
}

/** True when text contains an explicit numeric word-count requirement (not descriptive source length). */
export function textContainsExplicitWordCountTarget(text: string): boolean {
  return parseExplicitWordCountFromText(text) !== null;
}

/** Single display value for thinking chiclets — target wins, then minimum. */
export function resolveChatSpecifiedWordLength(text: string): number | null {
  const parsed = parseExplicitWordCountFromText(text);
  if (!parsed) return null;
  if (parsed.targetWords != null) return parsed.targetWords;
  if (parsed.minimumWords != null) return parsed.minimumWords;
  return null;
}
